import React from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo, stats } from '../data/mock';

const HeroSection = () => {
  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 via-white to-gray-50">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23002060' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      {/* Gradient Orbs */}
      <div className="absolute top-20 right-20 w-96 h-96 bg-[#002060]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-20 w-80 h-80 bg-gray-300/20 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20">
        <div className="text-center">
          {/* Logo Mark */}
          <div className="flex justify-center mb-8">
            <div className="flex items-baseline gap-1">
              <span className="text-6xl md:text-7xl font-serif font-bold text-[#002060] tracking-tight">D</span>
              <span className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-b from-gray-300 via-gray-400 to-gray-500 bg-clip-text text-transparent tracking-tight">M</span>
              <span className="text-6xl md:text-7xl font-serif font-bold text-[#002060] tracking-tight">G</span>
            </div>
          </div>

          {/* Company Name */}
          <div className="mb-6">
            <div className="flex items-center justify-center gap-4 mb-2">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-gray-400" />
              <p className="text-sm md:text-base tracking-[0.3em] text-[#002060] font-medium">
                DUPONT MANAGEMENT GROUP
              </p>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-gray-400" />
            </div>
          </div>

          {/* Tagline */}
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-serif font-light text-gray-800 mb-6 leading-tight">
            Strategic Leadership for a
            <span className="block text-[#002060] font-medium">Complex World</span>
          </h1>

          {/* Description */}
          <p className="max-w-3xl mx-auto text-lg md:text-xl text-gray-600 mb-10 leading-relaxed">
            A global management consultancy specializing in renewable energy, food security, 
            mining operations, crisis management, and security solutions across emerging markets.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Button
              onClick={(e) => scrollToSection(e, '#contact')}
              className="bg-[#002060] hover:bg-[#001040] text-white px-8 py-6 text-base font-medium tracking-wide transition-all duration-300 group"
            >
              Request Consultation
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              variant="outline"
              onClick={(e) => scrollToSection(e, '#services')}
              className="border-[#002060] text-[#002060] hover:bg-[#002060] hover:text-white px-8 py-6 text-base font-medium tracking-wide transition-all duration-300"
            >
              Explore Our Services
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center p-6 rounded-lg bg-white/50 backdrop-blur-sm border border-gray-100 hover:border-[#002060]/20 transition-all duration-300 hover:shadow-lg"
              >
                <p className="text-3xl md:text-4xl font-serif font-bold text-[#002060] mb-1">
                  {stat.value}
                </p>
                <p className="text-sm text-gray-600 tracking-wide">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <a
            href="#about"
            onClick={(e) => scrollToSection(e, '#about')}
            className="text-gray-400 hover:text-[#002060] transition-colors"
          >
            <ChevronDown size={32} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
